import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { searchContent } from '@plone/volto/actions/search/search';
import config from '@plone/volto/registry';
import qs from 'query-string';

const encodeSearchOptions = (options: Record<string, any>) => {
  const encoded: Record<string, any> = {};
  for (const [key, value] of Object.entries(options)) {
    if (typeof value === 'string') {
      encoded[key] = encodeURIComponent(value);
    } else if (Array.isArray(value)) {
      encoded[key] = value.map((item) =>
        typeof item === 'string' ? encodeURIComponent(item) : item,
      );
    } else {
      encoded[key] = value;
    }
  }
  return encoded;
};

interface SearchReduxState {
  items?: Array<{
    '@id': string;
    title: string;
    review_state?: string;
    [key: string]: any;
  }>;
  total?: number;
  batching?: unknown;
  loading?: boolean;
  loaded?: boolean;
}

interface UseSearchResultsResult {
  items: any[];
  itemsTotal: number;
  batching: any;
  loading: boolean;
  loaded: boolean;
  currentPage: number;
  handlePaginationChange: (activePage: number) => void;
}

export const useSearchResults = (): UseSearchResultsResult => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);

  const defaultPageSize = config.settings?.defaultPageSize || 25;

  const {
    items,
    total: itemsTotal,
    batching,
    loading,
    loaded,
  } = useSelector((state: { search: SearchReduxState }) => {
    const searchState = state.search || {};
    // Volto returns 'items_total', not 'total'
    return {
      ...searchState,
      total: searchState.total || (searchState as any).items_total,
    };
  });

  const doSearch = useCallback(() => {
    const options = qs.parse(location.search);
    setCurrentPage(1);
    const searchOptions = {
      b_size: defaultPageSize,
      use_site_search_settings: 1,
      ...encodeSearchOptions(options),
    };
    dispatch(searchContent('', searchOptions) as any);
  }, [dispatch, location.search, defaultPageSize]);

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  useEffect(() => {
    if (
      typeof document !== 'undefined' &&
      document.querySelector('#search-results')
    ) {
      setTimeout(() => {
        const el = document.querySelector('#search-results');
        if (el) {
          // `preventScroll` porque `handlePaginationChange` acabou de subir ao
          // topo: sem ele o foco puxa a página de volta para os resultados
          // 100ms depois, e cada troca de página sobe e desce.
          (el as HTMLElement).focus({ preventScroll: true });
        }
      }, 100);
    }
  }, [items]);

  const handlePaginationChange = useCallback(
    (activePage: number) => {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
      let options = qs.parse(location.search);
      options['use_site_search_settings'] = 1;

      setCurrentPage(activePage);
      dispatch(
        searchContent('', {
          b_size: defaultPageSize,
          ...encodeSearchOptions(options),
          b_start:
            (activePage - 1) * (Number(options.b_size) || defaultPageSize),
        }) as any,
      );
    },
    [dispatch, location.search, defaultPageSize],
  );

  return {
    items: items || [],
    itemsTotal: itemsTotal || 0,
    batching,
    loading: loading || false,
    loaded: loaded || false,
    currentPage,
    handlePaginationChange,
  };
};

export default useSearchResults;
